import apiClient from './apiClient';
import { PUBLIC_API_URL, AUTH_API_URL } from '@/config/env';
import { useAppStateStore } from '@/store/appStateStore';
import { writeAppliedSavedCache } from '@/features/home/utils/appliedSavedCache';
import type {
  SearchResponse,
  SearchInput,
  AggregationValue,
  AppliedSavedModel,
  JobCountsResponse,
  JobEntity,
  SavedJobsResponse,
} from '@/types/job.types';

// ---------------------------------------------------------------------------
// Query-param builder — mirrors Flutter IWebHomeRepository.getQueryParams()
// ---------------------------------------------------------------------------

function buildSearchQueryParams(searchInput?: SearchInput): string {
  if (!searchInput) return '';

  const parts: string[] = [];

  if (searchInput.skills && searchInput.switchIndex === 0) {
    const encoded = searchInput.skills.split(',').join('$:');
    parts.push(`skills=${encoded}`);
  } else if (searchInput.roles && searchInput.switchIndex === 1) {
    parts.push(`roles=${searchInput.roles}`);
  }

  if (searchInput.locations) {
    parts.push(`locations=${searchInput.locations}`);
  }

  if (searchInput.isHotJob) {
    parts.push('hotJob=true');
  }

  if (searchInput.filters && Object.keys(searchInput.filters).length > 0) {
    const filterParts: string[] = [];
    for (const [key, values] of Object.entries(searchInput.filters)) {
      if (key === 'sortBy' || !values.length) continue;

      const encoded = values.map((v: AggregationValue) => {
        if (v.catgKey === 'selectedSortField') {
          return v.additionalFields?.key ?? '';
        }
        const prefix = key === 'skills' ? '~' : '';
        return encodeURIComponent(`${prefix}${v.key ?? v.displayKey ?? ''}`);
      });

      filterParts.push(`${key}=${encoded.join('$:')}`);
    }
    if (filterParts.length) {
      parts.push(filterParts.join('&'));
    }
  }

  return parts.join('&');
}

// ---------------------------------------------------------------------------
// Search jobs — GET /v1/p/requisition/_search
// ---------------------------------------------------------------------------

export async function searchJobs(
  searchInput?: SearchInput,
  page = 1,
  size = 6,
): Promise<SearchResponse> {
  const qp = buildSearchQueryParams(searchInput);
  const url = `${PUBLIC_API_URL}/requisition/_search?page=${page}&size=${size}${qp ? `&${qp}` : ''}`;
  const res = await apiClient.get<SearchResponse>(url);
  return res.data;
}

// ---------------------------------------------------------------------------
// Aggregations — GET /v1/p/requisition/aggregation/_search
// ---------------------------------------------------------------------------

export async function getSearchAggregations(
  searchInput?: SearchInput,
): Promise<SearchResponse> {
  const qp = buildSearchQueryParams(searchInput);
  const url = `${PUBLIC_API_URL}/requisition/aggregation/_search${qp ? `?${qp}` : ''}`;
  const res = await apiClient.get<SearchResponse>(url);
  return res.data;
}

// ---------------------------------------------------------------------------
// Job counts — GET /v1/p/requisition/_count
// ---------------------------------------------------------------------------

export async function getJobCounts(
  searchInput?: SearchInput,
): Promise<JobCountsResponse> {
  const qp = buildSearchQueryParams(searchInput);
  const url = `${PUBLIC_API_URL}/requisition/_count${qp ? `?${qp}` : ''}`;
  // Flutter getJobsCounts uses plain http (no HttpInterCeptor)
  const res = await apiClient.get<JobCountsResponse>(url, { skipErrorInterceptor: true });
  return res.data;
}

// ---------------------------------------------------------------------------
// Applied / saved status — GET /v1/candidate/applied-saved-jobs
// ---------------------------------------------------------------------------

export async function getAppliedSavedJobs(): Promise<AppliedSavedModel> {
  // Flutter getAppliedSavedJobs uses plain http (no HttpInterCeptor)
  const res = await apiClient.get<AppliedSavedModel>(
    `${AUTH_API_URL}/candidate/applied-saved-jobs`,
    { skipErrorInterceptor: true },
  );
  // Flutter's repository caches every successful response (LocalDataService.putAppliedSavedJobs)
  writeAppliedSavedCache(res.data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Saved jobs count — GET /v1/candidate/_savedJobs/count
// ---------------------------------------------------------------------------

export async function getSavedJobsCount(): Promise<number> {
  const res = await apiClient.get(`${AUTH_API_URL}/candidate/_savedJobs/count`);
  const count = typeof res.data === 'number' ? res.data : Number(res.data);
  // Header badge — Flutter AppStateNotifier.changeJobCount after every count refresh
  if (Number.isFinite(count)) useAppStateStore.getState().setSavedJobsCount(count);
  return count;
}

// ---------------------------------------------------------------------------
// Saved jobs list — GET /v1/candidate/_savedJobs (no offset/limit, so the backend's defaults)
// Mirrors Flutter ISavedJobsRepository.getSavedJobs(): plain http with only Content-Type and
// Authorization. Flutter parses the body as SearchWithAggrModel ({ entities, total }), but the
// backend returns a plain List<RequisitionResponseBean> (CandidateJobsController.getSavedJobs),
// so Flutter's page always fails. That is treated as a Flutter bug (doc/MIGRATION_GAP_PLAN.md,
// D2): the array is read, and the { entities } shape is still accepted.
// ---------------------------------------------------------------------------

export async function getSavedJobsList(): Promise<SavedJobsResponse> {
  const res = await apiClient.get<JobEntity[] | Partial<SavedJobsResponse>>(
    `${AUTH_API_URL}/candidate/_savedJobs`,
    { skipErrorInterceptor: true, authHeaderOnly: true },
  );
  if (res.status !== 200 || !res.data || typeof res.data !== 'object') {
    throw new Error('Failed to load saved jobs');
  }
  if (Array.isArray(res.data)) {
    return { entities: res.data, total: res.data.length };
  }
  return {
    entities: Array.isArray(res.data.entities) ? res.data.entities : [],
    total: res.data.total,
  };
}

// ---------------------------------------------------------------------------
// Save job — PUT /v1/candidate/_savedJobs
// ---------------------------------------------------------------------------

export async function saveJob(payload: Record<string, unknown>): Promise<boolean> {
  const res = await apiClient.put(`${AUTH_API_URL}/candidate/_savedJobs`, payload);
  return res.status === 200;
}

// ---------------------------------------------------------------------------
// Unsave job — DELETE /v1/candidate/_savedJobs?displayId=X
// ---------------------------------------------------------------------------

export async function unsaveJob(displayId: string): Promise<boolean> {
  const res = await apiClient.delete(
    `${AUTH_API_URL}/candidate/_savedJobs?displayId=${displayId}`,
  );
  return res.status === 200;
}

// ---------------------------------------------------------------------------
// Job details — GET /v1/p/requisition/displayId/:displayId
// Mirrors Flutter IJobDetailsViewRepository.getJobDetails() (plain http, not the Dio interceptor)
// ---------------------------------------------------------------------------

export async function getJobDetails(displayId: string): Promise<JobEntity> {
  const res = await apiClient.get<JobEntity>(
    `${PUBLIC_API_URL}/requisition/displayId/${displayId}`,
    { skipErrorInterceptor: true },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Similar jobs — GET /v1/p/requisition/:displayId/_similar
// Mirrors Flutter IJobDetailsViewRepository.getSimilarJobs() (plain http, not the Dio interceptor)
// ---------------------------------------------------------------------------

export async function getSimilarJobs(displayId: string): Promise<JobEntity[]> {
  const res = await apiClient.get<JobEntity[]>(
    `${PUBLIC_API_URL}/requisition/${displayId.trim()}/_similar`,
    { skipErrorInterceptor: true },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Job share referral code — GET /v1/p/job/_auth/:platformName/:jobId
// Mirrors Flutter IJobConfigRepository.getJobReferralCode() (plain http, not the Dio interceptor)
// ---------------------------------------------------------------------------

export interface JobReferralCode {
  channel: string;
  userEmailId: string;
  jobId: string;
}

export async function getJobReferralCode(
  jobId: string,
  platformName: string,
): Promise<JobReferralCode> {
  const res = await apiClient.get<Partial<JobReferralCode>>(
    `${PUBLIC_API_URL}/job/_auth/${platformName}/${jobId}`,
    { skipErrorInterceptor: true },
  );
  if (!res.data || typeof res.data !== 'object') throw new Error('Empty referral response');
  return {
    channel: res.data.channel ?? '',
    userEmailId: res.data.userEmailId ?? '',
    jobId: res.data.jobId ?? '',
  };
}

// ---------------------------------------------------------------------------
// Search bar suggestions — mirrors Flutter IJobSearchBarRepository
//   GET /v1/p/skill/_suggest?key=&page=      → [{ skill }]
//   GET /v1/p/role/_suggest?key=&page=       → [{ roleSkill }]
//   GET /v1/p/location/_suggest?page=&placeType=city&key= → [{ fqLocationName }]
// All three use plain http in Flutter (no HttpInterCeptor).
// ---------------------------------------------------------------------------

export async function getSkillSuggestions(key: string, page = 1): Promise<string[]> {
  const res = await apiClient.get<{ skill?: string }[]>(
    `${PUBLIC_API_URL}/skill/_suggest?key=${encodeURIComponent(key)}&page=${page}`,
    { skipErrorInterceptor: true },
  );
  return (res.data ?? []).map((item) => item.skill ?? '').filter(Boolean);
}

export async function getRoleSuggestions(key: string, page = 1): Promise<string[]> {
  const res = await apiClient.get<{ roleSkill?: string }[]>(
    `${PUBLIC_API_URL}/role/_suggest?key=${encodeURIComponent(key)}&page=${page}`,
    { skipErrorInterceptor: true },
  );
  return (res.data ?? []).map((item) => item.roleSkill ?? '').filter(Boolean);
}

export async function getSearchLocationSuggestions(key: string, page = 1): Promise<string[]> {
  const keyParam = key ? `&key=${encodeURIComponent(key)}` : '';
  const res = await apiClient.get<{ fqLocationName?: string }[]>(
    `${PUBLIC_API_URL}/location/_suggest?page=${page}&placeType=city${keyParam}`,
    { skipErrorInterceptor: true },
  );
  return (res.data ?? []).map((item) => item.fqLocationName ?? '').filter(Boolean);
}

// ---------------------------------------------------------------------------
// Re-export buildSearchQueryParams for use in Jobs listing hook
// ---------------------------------------------------------------------------

export { buildSearchQueryParams };
